import { walletDisplayName } from '@/lib/displayName';
import type {
  EvmAddress,
  LeaderboardEntry,
  LinkedSocials,
  Quest,
  QuestCategory,
  QuestIconName,
  QuestPrerequisite,
  QuestSection,
  QuestStatus,
  QuestType,
  QuestVerifyMethod,
  ParticipantProfile,
  SocialProvider,
  Submission,
  SubmissionCategory,
  SubmissionIconName,
  SubmissionLimits,
  UserProfile,
} from '@/lib/types';

// ─── Backend wire types ─────────────────────────────────────────────────────

type BackendQuestType =
  | 'visit_link'
  | 'tg_join'
  | 'discord_join'
  | 'x_follow'
  | 'x_like'
  | 'x_retweet'
  | 'bonus';

type BackendSocialPlatform = 'telegram' | 'discord' | 'x';

export interface BackendQuest {
  id: string;
  code: string;
  type: BackendQuestType;
  title: string;
  description: string | null;
  points: number;
  config: Record<string, unknown>;
  order_index: number;
  completed: boolean;
  /** False only on a bonus quest whose prerequisites are incomplete. */
  unlocked?: boolean;
}

interface BackendLinkedAccount {
  platform: BackendSocialPlatform;
  handle: string | null;
}

export interface BackendUserPublic {
  id: string;
  wallet_address: string;
  total_points: number;
  created_at: string;
}

export interface BackendMe extends BackendUserPublic {
  rank: number;
  completions_count: number;
  social_accounts: BackendLinkedAccount[];
}

export interface BackendVerifyResponse {
  access_token: string;
  token_type: string;
  user: BackendUserPublic;
}

export interface BackendLeaderboardEntry {
  userId: string;
  walletAddress: string | null;
  rail2Points: number | null;
  rail1Points: number | null;
  totalPoints: number;
  currentBand: string | null;
  currentStreak: number | null;
  isUser: boolean;
  /** SQL RANK() — ties share a rank. Arrives as a string (Postgres bigint). */
  rank: number | string;
}

export interface BackendPublicConfig {
  privy_app_id: string;
  privy_client_id: string;
  telegram_bot_username: string;
  discord_enabled: boolean;
}

export interface BackendCompletionResult {
  quest_id: string;
  completed_at: string;
  points_awarded: number;
  total_points: number;
}

export interface BackendTask {
  id: string;
  title: string;
  description: string | null;
  category: string;
  basePoints: number;
  isHighValue: boolean;
}

export interface BackendSubmissionRow {
  id: string;
  taskId: string | null;
  status: 'pending' | 'approved' | 'rejected' | 'under_review';
  imageUrl: string | null;
  aiConfidence: number | null; // 0..100
  aiVerdict: string | null;
  createdAt: string;
}

export interface BackendSubmissionHistoryRow extends BackendSubmissionRow {
  taskTitle: string;
  basePoints: number;
}

export interface BackendSubmissionLimits {
  cooldown: {
    active: boolean;
    remainingSeconds: number;
    nextSubmissionAt: string | null;
  };
  today: {
    submissionsUsed: number;
    submissionsLimit: number;
    highValueUsed: number;
    highValueLimit: number;
  };
}

// getParticipantProfile left-joins sustainabilityScore/monthlyTally/userStreaks,
// which are created lazily on a member's first scored submission — every
// field below can be null for a brand-new participant.
export interface BackendParticipantProfile {
  totalScore: number | null;
  globalRank: number | null;
  currentBand: string | null;
  multiplier: number | null;
  monthlyTally: number | null;
  currentStreak: number | null;
  telegramId: string | null;
  rail1score: number | null;
  rail2score: number | null;
  isFlagged: boolean | null;
}

// ─── Quest adapter ──────────────────────────────────────────────────────────

const QUEST_META: Record<
  BackendQuestType,
  {
    category: QuestCategory;
    type: QuestType;
    icon: QuestIconName;
    verifyMethod: QuestVerifyMethod;
    requiresSocial?: SocialProvider;
    cooldownSeconds?: number;
    ctaLabel?: string;
  }
> = {
  visit_link: {
    category: 'onboarding',
    type: 'visit',
    icon: 'bigwater',
    verifyMethod: 'manual',
    cooldownSeconds: 5,
    ctaLabel: 'Open link',
  },
  // Joining/following BigWater's own channels is one-time setup → Onboarding.
  // Post engagement (x_like / x_retweet) is recurring / cross-marketing and
  // stays under Social.
  tg_join: {
    category: 'onboarding',
    type: 'telegram_join',
    icon: 'message-circle',
    verifyMethod: 'oauth',
    requiresSocial: 'telegram',
    ctaLabel: 'Open group',
  },
  discord_join: {
    category: 'onboarding',
    type: 'discord_join',
    icon: 'hash',
    verifyMethod: 'oauth',
    requiresSocial: 'discord',
    ctaLabel: 'Open server',
  },
  x_follow: {
    category: 'onboarding',
    type: 'twitter_follow',
    icon: 'x',
    // Still server-verified (follower snapshot) at claim time. `manual` only
    // reuses the visit_link gating: open the link, wait out the cooldown,
    // then the claim button goes live.
    verifyMethod: 'manual',
    requiresSocial: 'x',
    cooldownSeconds: 15,
    ctaLabel: 'Follow on X',
  },
  x_like: {
    category: 'social',
    type: 'twitter_post_engage',
    icon: 'star',
    // Server-verified (likers snapshot), same as x_retweet.
    verifyMethod: 'oauth',
    requiresSocial: 'x',
    ctaLabel: 'Like on X',
  },
  x_retweet: {
    category: 'social',
    type: 'twitter_post_engage',
    icon: 'repeat-2',
    // Server-verified (retweeters snapshot): no open-the-link-first gating;
    // the claim button is live and the backend is the judge.
    verifyMethod: 'oauth',
    requiresSocial: 'x',
    ctaLabel: 'Retweet on X',
  },
  bonus: {
    category: 'bonus',
    type: 'bonus',
    icon: 'gift',
    verifyMethod: 'auto',
    ctaLabel: 'Claim bonus',
  },
};

function ctaUrlFromConfig(
  type: BackendQuestType,
  config: Record<string, unknown>,
): string | undefined {
  if (type === 'visit_link' && typeof config.url === 'string') return config.url;
  if ((type === 'tg_join' || type === 'discord_join') && typeof config.invite_url === 'string')
    return config.invite_url;
  if ((type === 'x_retweet' || type === 'x_like') && typeof config.post_url === 'string')
    return config.post_url;
  if (type === 'x_follow' && typeof config.target_handle === 'string') {
    const handle = (config.target_handle as string).replace(/^@/, '');
    return `https://x.com/${handle}`;
  }
  if ((type === 'x_like' || type === 'x_retweet') && typeof config.tweet_id === 'string')
    return `https://x.com/i/web/status/${config.tweet_id}`;
  return undefined;
}

/**
 * Quest types whose backend verifier is not implemented yet. We surface the
 * cards in the UI but mark them as `coming_soon` so users see what's coming
 * without being able to claim. Drop the entry once the backend verifier ships.
 */
// Every backend type is claimable now; kept for the next parked type.
const COMING_SOON_TYPES: ReadonlySet<BackendQuestType> = new Set([]);

/** A `visit_link` quest pointing at X (e.g. "Follow us on X" configured as a
 *  plain link) gets the X mark instead of the BigWater logomark. */
function isXUrl(url: string | undefined): boolean {
  if (!url) return false;
  try {
    return /^(www\.)?(x|twitter)\.com$/i.test(new URL(url).hostname);
  } catch {
    return false;
  }
}

export function adaptQuest(
  q: BackendQuest,
  titlesByCode?: Map<string, string>,
  completedCodes?: ReadonlySet<string>,
): Quest {
  const meta = QUEST_META[q.type];
  // `unlocked === false` only ever comes on bonus quests: the server computed
  // that some prerequisites are missing (and enforces it at claim time).
  const status: QuestStatus = q.completed
    ? 'completed'
    : q.unlocked === false
      ? 'locked'
      : COMING_SOON_TYPES.has(q.type)
        ? 'coming_soon'
        : 'available';
  // A bonus quest names its prerequisites: resolve each to a title +
  // completion state so the UI can render a checklist (and, while locked, a
  // plain-text hint for the card / screen readers). Resolved even when the
  // server says unlocked — `applyBonusBotGate` may still lock the quest on the
  // client-side bot step and needs the full list to show alongside it.
  let requirementHint: string | undefined;
  let prerequisites: QuestPrerequisite[] | undefined;
  if (q.type === 'bonus' && !q.completed) {
    const codes = (q.config.required_quest_codes as string[] | undefined) ?? [];
    prerequisites = codes.map((code) => ({
      code,
      title: titlesByCode?.get(code) ?? code,
      completed: completedCodes?.has(code) ?? false,
    }));
    if (status === 'locked') {
      const names = prerequisites.map((p) => p.title);
      requirementHint = names.length
        ? `Complete first: ${names.join(', ')}`
        : 'Complete the other quests first';
    }
  }
  const ctaUrl = ctaUrlFromConfig(q.type, q.config);
  return {
    requirementHint,
    prerequisites,
    id: q.id,
    slug: q.code,
    title: q.title,
    description: q.description ?? '',
    category: meta.category,
    type: meta.type,
    points: q.points,
    status,
    icon: q.type === 'visit_link' && isXUrl(ctaUrl) ? 'x' : meta.icon,
    ctaLabel: meta.ctaLabel,
    ctaUrl,
    requiresSocial: meta.requiresSocial,
    verifyMethod: meta.verifyMethod,
    cooldownSeconds: meta.cooldownSeconds,
  };
}

const SECTION_TITLES: Record<QuestCategory, { title: string; subtitle: string }> = {
  onboarding: {
    title: 'Onboarding',
    subtitle: 'Start here — visit, join and follow BigWater to light up the board.',
  },
  social: {
    title: 'Social',
    subtitle: 'Recurring and campaign quests. New drops to boost, every wave.',
  },
  web3: {
    title: 'On-chain',
    subtitle: 'Proof-driven quests for wallets that show up early.',
  },
  bonus: {
    title: 'Bonus',
    subtitle: 'Finish the sets above, claim the extras.',
  },
};

export function groupQuestsIntoSections(quests: Quest[]): QuestSection[] {
  const buckets: Record<QuestCategory, Quest[]> = {
    onboarding: [],
    social: [],
    web3: [],
    bonus: [],
  };
  for (const q of quests) buckets[q.category].push(q);

  const order: QuestCategory[] = ['onboarding', 'social', 'web3', 'bonus'];
  return order
    .filter((cat) => buckets[cat].length > 0)
    .map((cat) => ({
      category: cat,
      title: SECTION_TITLES[cat].title,
      subtitle: SECTION_TITLES[cat].subtitle,
      quests: buckets[cat],
    }));
}

/**
 * Splices a client-synthesized quest (e.g. the Telegram bot connect card,
 * which comes from bigwater_backend/rail2 rather than the `/v1/quests`
 * catalogue) into the onboarding section, creating that section if the
 * catalogue happened to return none. `quest` is `null` when the synthetic
 * card shouldn't render at all (e.g. rail2 isn't configured).
 */
export function mergeSyntheticOnboardingQuest(
  sections: QuestSection[],
  quest: Quest | null,
): QuestSection[] {
  if (!quest) return sections;

  const existing = sections.find((s) => s.category === 'onboarding');
  if (!existing) {
    return [
      ...sections,
      {
        category: 'onboarding',
        title: SECTION_TITLES.onboarding.title,
        subtitle: SECTION_TITLES.onboarding.subtitle,
        quests: [quest],
      },
    ];
  }

  return sections.map((s) =>
    s.category === 'onboarding' ? { ...s, quests: [...s.quests, quest] } : s,
  );
}

// ─── Profile adapter ────────────────────────────────────────────────────────

function deterministicAvatar(address: string): string {
  const seed = encodeURIComponent(address.toLowerCase());
  return `https://api.dicebear.com/7.x/identicon/svg?seed=${seed}&backgroundColor=cfe3f3,d8e8d4,f0e6d2`;
}

function adaptLinkedSocials(accounts: BackendLinkedAccount[]): LinkedSocials {
  const out: LinkedSocials = {};
  for (const a of accounts) {
    if (a.platform === 'telegram') out.telegram = a.handle ?? '';
    else if (a.platform === 'discord') out.discord = a.handle ?? '';
    else if (a.platform === 'x') out.x = a.handle ?? '';
  }
  return out;
}

export function adaptMeToProfile(me: BackendMe): UserProfile {
  const address = me.wallet_address as EvmAddress;
  return {
    address,
    displayName: walletDisplayName(address),
    avatarUrl: deterministicAvatar(address),
    points: me.total_points,
    rank: me.rank,
    missionsCompleted: me.completions_count,
    // Referrals endpoint isn't shipped — defaults to 0; the page renders a
    // blurred preview with a "Coming soon" overlay until the API lands.
    referralsCount: 0,
    linkedSocials: adaptLinkedSocials(me.social_accounts),
    joinedAt: me.created_at,
  };
}

export function adaptVerifyToProfile(user: BackendUserPublic): UserProfile {
  const address = user.wallet_address as EvmAddress;
  return {
    address,
    displayName: walletDisplayName(address),
    avatarUrl: deterministicAvatar(address),
    points: user.total_points,
    rank: 0,
    missionsCompleted: 0,
    referralsCount: 0,
    linkedSocials: {},
    joinedAt: user.created_at,
  };
}

// ─── Leaderboard adapter ────────────────────────────────────────────────────

export function adaptLeaderboardEntry(entry: BackendLeaderboardEntry): LeaderboardEntry {
  const address = entry.walletAddress as EvmAddress;
  return {
    rank: Number(entry.rank),
    address,
    displayName: walletDisplayName(address),
    avatarUrl: deterministicAvatar(address),
    points: entry.totalPoints,
    isYou: entry.isUser,
  };
}

// ─── Submission adapters ────────────────────────────────────────────────────

// Task `category` is admin-entered free text (no seed data to enumerate it
// from yet) — this is a best-effort guess, not a contract. Revisit once real
// categories exist; unmapped values safely fall back to 'sparkles'.
const CATEGORY_ICON_MAP: Record<string, SubmissionIconName> = {
  recycling: 'recycle',
  transport: 'bike',
  waste: 'trash',
  energy: 'zap',
  planting: 'sprout',
};

function iconForCategory(category: string): SubmissionIconName {
  return CATEGORY_ICON_MAP[category.toLowerCase()] ?? 'sparkles';
}

function adaptAiConfidence(confidence: number | null): number | undefined {
  return confidence == null ? undefined : confidence / 100;
}

export function adaptTask(task: BackendTask): SubmissionCategory {
  return {
    key: task.id,
    label: task.title,
    hint: task.description ?? '',
    basePoints: task.basePoints,
    highValue: task.isHighValue,
    icon: iconForCategory(task.category),
  };
}

export function adaptSubmissionLimits(raw: BackendSubmissionLimits): SubmissionLimits {
  return {
    nextEligibleAt: raw.cooldown.nextSubmissionAt,
    submittedToday: raw.today.submissionsUsed,
    dailyCap: raw.today.submissionsLimit,
    highValueUsedToday: raw.today.highValueUsed,
    highValueCap: raw.today.highValueLimit,
  };
}

/** For submitProof's own response — the category is already known
 *  client-side (just submitted), so basePoints/label come from it directly
 *  rather than a lookup. */
export function adaptSubmission(
  row: BackendSubmissionRow,
  category: SubmissionCategory,
): Submission {
  return {
    id: row.id,
    categoryKey: row.taskId ?? category.key,
    categoryLabel: category.label,
    status: row.status,
    basePoints: category.basePoints,
    awardedPoints: row.status === 'approved' ? category.basePoints : 0,
    submittedAt: row.createdAt,
    aiVerdict: row.aiVerdict ?? undefined,
    aiConfidence: adaptAiConfidence(row.aiConfidence),
    imageUrl: row.imageUrl ?? undefined,
  };
}

/** For history rows — self-contained via the backend's taskTitle/basePoints
 *  join, no categories-list dependency (which would silently break for a
 *  submission whose task has since been deactivated). */
export function adaptSubmissionHistoryEntry(row: BackendSubmissionHistoryRow): Submission {
  return {
    id: row.id,
    categoryKey: row.taskId ?? 'custom',
    categoryLabel: row.taskTitle,
    status: row.status,
    basePoints: row.basePoints,
    awardedPoints: row.status === 'approved' ? row.basePoints : 0,
    submittedAt: row.createdAt,
    aiVerdict: row.aiVerdict ?? undefined,
    aiConfidence: adaptAiConfidence(row.aiConfidence),
    imageUrl: row.imageUrl ?? undefined,
  };
}

export function adaptParticipantProfile(raw: BackendParticipantProfile): ParticipantProfile {
  return {
    score: raw.totalScore ?? 0,
    rank: raw.globalRank ?? 0,
    band: raw.currentBand ?? 'seedling',
    multiplier: (raw.multiplier ?? 100) / 100,
    monthlyTally: raw.monthlyTally ?? 0,
    streakDays: raw.currentStreak ?? 0,
    telegramId: raw.telegramId ?? null,
    rail1Score: raw.rail1score ?? 0,
    rail2Score: raw.rail2score ?? 0,
    isFlagged: raw.isFlagged ?? false,
  };
}
